import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Trophy, Calendar, Users, Clock, MapPin, Award, ArrowLeft, Edit, Trash2, Eye, BarChart3, UserCheck, BookOpen } from 'lucide-react';
import { hackathonAPI } from '../../services/api';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { StatusBadge } from '../../components/ui/StatusBadge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { useToast } from '../../contexts/ToastContext';

interface Hackathon {
  _id: string;
  title: string;
  description: string;
  startDate: string;
  endDate: string;
  status: string;
  maxTeams: number;
  currentTeams: number;
  approvedTeams?: number;
  registrationDeadline: string;
  phases: Array<{
    _id: string;
    name: string;
    description: string;
    startDate: string;
    endDate: string;
    status: string;
    order: number;
  }>;
  problemStatements: Array<{
    _id: string;
    title: string;
    description: string;
    category: string;
    difficulty: string;
  }>;
  prizes: Array<{
    rank: number;
    description: string;
    value: string;
  }>;
  rules: string[];
  requirements: string[];
  createdBy: {
    firstName: string;
    lastName: string;
    email: string;
  };
  isActive: boolean;
}

const AdminHackathonDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { success, error } = useToast();
  const [hackathon, setHackathon] = useState<Hackathon | null>(null);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<any>(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  useEffect(() => {
    if (id) {
      fetchHackathon();
      fetchStats();
    }
  }, [id]);

  const fetchHackathon = async () => {
    try {
      setLoading(true);
      const response = await hackathonAPI.getHackathonById(id!);
      setHackathon(response);
    } catch (err: any) {
      error(err.response?.data?.message || 'Failed to fetch hackathon details');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await hackathonAPI.getHackathonDetailedStats(id!);
      setStats(response);
    } catch (err: any) {
      console.error('Failed to fetch stats:', err);
    }
  };

  const handleDelete = () => {
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    try {
      await hackathonAPI.deleteHackathon(id!);
      success('Hackathon deleted successfully');
      setShowDeleteDialog(false);
      // Redirect to hackathon management
      window.location.href = '/admin/hackathons';
    } catch (err: any) {
      error(err.response?.data?.message || 'Failed to delete hackathon');
      setShowDeleteDialog(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTimeRemaining = (deadline: string) => {
    const now = new Date();
    const deadlineDate = new Date(deadline);
    const diff = deadlineDate.getTime() - now.getTime();
    
    if (diff <= 0) {
      return 'Registration closed';
    }
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (days > 0) return `${days} days left`;
    if (hours > 0) return `${hours} hours left`;
    return `${minutes} minutes left`;
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
      case 'approved':
      case 'completed':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'pending':
      case 'forming':
      case 'upcoming':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'rejected':
      case 'cancelled':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'in_progress':
      case 'submitted':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!hackathon) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Hackathon Not Found</h1>
          <Link to="/admin/hackathons">
            <Button variant="outline">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Hackathon Management
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <Link to="/admin/hackathons" className="text-blue-600 hover:text-blue-800 mb-4 inline-block">
          <ArrowLeft className="h-4 w-4 inline mr-2" />
          Back to Hackathon Management
        </Link>
        
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">{hackathon.title}</h1>
            <div className="flex items-center gap-4 mb-4">
              <StatusBadge status={hackathon.status} />
              <Badge className="bg-blue-100 text-blue-800">
                {hackathon.currentTeams}/{hackathon.maxTeams} Teams
              </Badge>
            </div>
          </div>
          
          <div className="flex gap-2">
            <Link to={`/admin/hackathons/${hackathon._id}/edit`}>
              <Button variant="outline">
                <Edit className="h-4 w-4 mr-2" />
                Edit Hackathon
              </Button>
            </Link>
            <Link to={`/admin/teams?hackathon=${hackathon._id}`}>
              <Button variant="outline">
                <Users className="h-4 w-4 mr-2" />
                Manage Teams
              </Button>
            </Link>
            <Button
              variant="outline"
              onClick={handleDelete}
              className="text-red-600 hover:text-red-700"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </Button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="timeline">Timeline</TabsTrigger>
          <TabsTrigger value="problems">Problem Statements</TabsTrigger>
          <TabsTrigger value="teams">Teams</TabsTrigger>
          <TabsTrigger value="prizes">Prizes</TabsTrigger>
          <TabsTrigger value="rules">Rules & Requirements</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Basic Information */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Trophy className="h-5 w-5 text-yellow-500" />
                  Basic Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h4 className="font-semibold mb-2">Description</h4>
                  <p className="text-gray-700">{hackathon.description}</p>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Start Date</p>
                    <p className="font-medium">{formatDate(hackathon.startDate)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">End Date</p>
                    <p className="font-medium">{formatDate(hackathon.endDate)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Registration Deadline</p>
                    <p className="font-medium">{formatDate(hackathon.registrationDeadline)} at {formatTime(hackathon.registrationDeadline)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Time Remaining for Registration</p>
                    <p className="font-medium">{getTimeRemaining(hackathon.registrationDeadline)}</p>
                  </div>
                </div>
                
                <div>
                  <p className="text-sm text-gray-600">Created By</p>
                  <p className="font-medium">{hackathon.createdBy.firstName} {hackathon.createdBy.lastName}</p>
                  <p className="text-sm text-gray-500">{hackathon.createdBy.email}</p>
                </div>
              </CardContent>
            </Card>

            {/* Statistics */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5 text-blue-500" />
                  Statistics
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <div className="text-2xl font-bold text-blue-600">{stats?.registeredUsers || 0}</div>
                    <div className="text-sm text-gray-600">Registered Users</div>
                  </div>
                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <div className="text-2xl font-bold text-green-600">{stats?.approvedTeams || 0} / {hackathon.maxTeams}</div>
                    <div className="text-sm text-gray-600">Approved Teams / Maximum Teams</div>
                  </div>
                  <div className="text-center p-4 bg-purple-50 rounded-lg">
                    <div className="text-2xl font-bold text-purple-600">{hackathon.phases?.length || 0}</div>
                    <div className="text-sm text-gray-600">Phases</div>
                  </div>
                  <div className="text-center p-4 bg-orange-50 rounded-lg">
                    <div className="text-2xl font-bold text-orange-600">{hackathon.problemStatements?.length || 0}</div>
                    <div className="text-sm text-gray-600">Problem Statements</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="timeline" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-blue-500" />
                Timeline & Phases
              </CardTitle>
            </CardHeader>
            <CardContent>
              {hackathon.phases && hackathon.phases.length > 0 ? (
                <div className="space-y-4">
                  {hackathon.phases.map((phase, index) => (
                    <div key={phase._id} className="border-l-4 border-blue-500 pl-4 py-2">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-semibold">{phase.name}</h4>
                          <p className="text-gray-600 text-sm">{phase.description}</p>
                          <p className="text-gray-500 text-xs">
                            {formatDate(phase.startDate)} - {formatDate(phase.endDate)}
                          </p>
                        </div>
                        <StatusBadge status={phase.status} />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Clock className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No phases defined</h3>
                  <p className="text-gray-600">Timeline will be available once phases are added</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="problems" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BookOpen className="h-5 w-5 text-green-500" />
                Problem Statements
              </CardTitle>
            </CardHeader>
            <CardContent>
              {hackathon.problemStatements && hackathon.problemStatements.length > 0 ? (
                <div className="space-y-4">
                  {hackathon.problemStatements.map((problem, index) => (
                    <Card key={problem._id} className="border-l-4 border-green-500">
                      <CardHeader>
                        <div className="flex items-start justify-between">
                          <CardTitle className="text-lg">{problem.title}</CardTitle>
                          <div className="flex gap-2">
                            <Badge className="bg-blue-100 text-blue-800">{problem.category}</Badge>
                            <Badge className={`${
                              problem.difficulty === 'easy' ? 'bg-green-100 text-green-800' :
                              problem.difficulty === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {problem.difficulty.toUpperCase()}
                            </Badge>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <p className="text-gray-700">{problem.description}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <BookOpen className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No problem statements</h3>
                  <p className="text-gray-600">Problem statements will be available once added</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="teams" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5 text-purple-500" />
                Team Management
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center py-8">
                <Users className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Team Management</h3>
                <p className="text-gray-600 mb-4">Manage teams, assignments, and submissions</p>
                <Link to={`/admin/teams?hackathon=${hackathon._id}`}>
                  <Button>
                    <UserCheck className="h-4 w-4 mr-2" />
                    Go to Team Management
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="prizes" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Award className="h-5 w-5 text-yellow-500" />
                Prizes
              </CardTitle>
            </CardHeader>
            <CardContent>
              {hackathon.prizes && hackathon.prizes.length > 0 ? (
                <div className="space-y-4">
                  {hackathon.prizes.map((prize, index) => (
                    <Card key={index} className="border-l-4 border-yellow-500">
                      <CardHeader>
                        <div className="flex items-center mb-2">
                          <Award className="h-5 w-5 mr-2 text-yellow-500" />
                          <CardTitle>{prize.description}</CardTitle>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <p className="text-lg font-bold text-blue-600">{prize.value}</p>
                        <p className="text-sm text-gray-500 mt-1">Rank: {prize.rank}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Award className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No prizes defined</h3>
                  <p className="text-gray-600">Prizes will be available once added</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="rules" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="h-5 w-5 text-red-500" />
                Rules & Requirements
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {hackathon.rules && hackathon.rules.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold mb-3">Rules</h3>
                  <ul className="list-disc list-inside space-y-2">
                    {hackathon.rules.map((rule, index) => (
                      <li key={index} className="text-gray-700">{rule}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              {hackathon.requirements && hackathon.requirements.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold mb-3">Requirements</h3>
                  <ul className="list-disc list-inside space-y-2">
                    {hackathon.requirements.map((requirement, index) => (
                      <li key={index} className="text-gray-700">{requirement}</li>
                    ))}
                  </ul>
                </div>
              )}

              {(!hackathon.rules || hackathon.rules.length === 0) && 
               (!hackathon.requirements || hackathon.requirements.length === 0) && (
                <div className="text-center py-12">
                  <MapPin className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No rules or requirements defined</h3>
                  <p className="text-gray-600">Rules and requirements will be available once added</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      
      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Delete</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p>Are you sure you want to delete this hackathon <strong>{hackathon?.title}</strong>?</p>
            <p className="text-sm text-gray-600 mt-2">This action cannot be undone.</p>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmDelete}>
              Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminHackathonDetail; 